import { Link } from "react-router-dom";
import { ArrowRight, FileText, ImageIcon, Sparkles, ShieldCheck, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useT, useI18n } from "../context/I18nContext";

export default function Landing() {
  const { user } = useAuth();
  const t = useT();
  const { lang } = useI18n();
  const isAr = lang === "ar";

  return (
    <div className="relative paper-bg" data-testid="landing-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="pill mb-6" data-testid="hero-eyebrow">
              <Sparkles size={12} /> {t("landing.eyebrow")}
            </span>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0F172A]">
              {t("landing.hero.line1")}<br/>
              <span className="italic text-[#881337]">{t("landing.hero.line2")}</span> {t("landing.hero.line2b")}
            </h1>
            <p className="mt-6 text-lg text-[#475569] max-w-xl leading-relaxed">
              {t("landing.hero.desc")}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={user ? "/tools/image-to-pdf" : "/register"} data-testid="hero-cta-image">
                <Button size="lg" className="bg-[#0F172A] hover:bg-[#1E293B] text-white h-12 px-6">
                  <ImageIcon size={16} className={isAr ? "ml-2" : "mr-2"}/> {t("landing.cta.convert")}
                  <ArrowRight size={16} className={isAr ? "mr-2 rotate-180" : "ml-2"}/>
                </Button>
              </Link>
              <Link to={user ? "/tools/cv-builder" : "/register"} data-testid="hero-cta-cv">
                <Button size="lg" variant="outline" className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white h-12 px-6">
                  <FileText size={16} className={isAr ? "ml-2" : "mr-2"}/> {t("landing.cta.cv")}
                </Button>
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-[#64748B]">
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#059669]"/> {t("landing.badge.jwt")}</span>
              <span className="flex items-center gap-2"><CreditCard size={16} className="text-[#D97706]"/> {t("landing.badge.pricing")}</span>
              <span className="flex items-center gap-2"><Sparkles size={16} className="text-[#881337]"/> {t("landing.badge.ai")}</span>
            </div>
          </div>

          {/* Preview card (English sample stays as-is — represents the CV output style) */}
          <div className="lg:col-span-5" dir="ltr">
            <div className="relative">
              <div className="card-classic p-6 relative overflow-hidden">
                <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#D97706]/10 blur-2xl" />
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-jb text-[11px] uppercase tracking-wider text-[#64748B]">{t("landing.preview.label")}</span>
                  <span className="pill">PDF</span>
                </div>
                <div className="border-b border-[#0F172A] pb-3 mb-3">
                  <div className="font-serif-display text-2xl font-bold text-center">Jonathan A. Reyes</div>
                  <div className="font-serif-display italic text-center text-[#475569] text-sm">Senior Product Designer</div>
                  <div className="text-center text-xs text-[#64748B] mt-1 font-mono-jb">jonathan@reyes.co · +1 (415) 555-0134 · San Francisco</div>
                </div>
                <div className="space-y-3 text-[13px] leading-6 text-[#334155]">
                  <div>
                    <div className="font-serif-display text-[#881337] font-bold text-sm tracking-wide">EXPERIENCE</div>
                    <div className="mt-1"><span className="font-semibold text-[#0F172A]">Design Lead — Northwind</span><span className="italic text-[#64748B]"> · 2021 – Present</span></div>
                    <div>• Led rebrand across 6 product surfaces</div>
                    <div>• Shipped design system used by 40 engineers</div>
                  </div>
                  <div>
                    <div className="font-serif-display text-[#881337] font-bold text-sm tracking-wide">EDUCATION</div>
                    <div className="mt-1"><span className="font-semibold text-[#0F172A]">B.A. Visual Communication — RISD</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid md:grid-cols-2 gap-6" data-testid="tools-grid">
          <ToolCard
            testId="tool-card-image-to-pdf"
            to={user ? "/tools/image-to-pdf" : "/register"}
            icon={<ImageIcon size={20}/>}
            title={t("landing.tool.image.title")}
            copy={t("landing.tool.image.copy")}
            tag={t("landing.tool.image.tag")}
            openLabel={t("landing.tool.open")}
            isAr={isAr}
          />
          <ToolCard
            testId="tool-card-cv-builder"
            to={user ? "/tools/cv-builder" : "/register"}
            icon={<FileText size={20}/>}
            title={t("landing.tool.cv.title")}
            copy={t("landing.tool.cv.copy")}
            tag={t("landing.tool.cv.tag")}
            openLabel={t("landing.tool.open")}
            isAr={isAr}
          />
        </div>

        <div className="mt-10 card-classic p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="font-serif-display text-2xl font-semibold">{t("landing.pricing.title")}</div>
            <div className="text-[#475569] mt-1">{t("landing.pricing.copy")}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cv/sample-pdf`} target="_blank" rel="noreferrer" data-testid="landing-cta-sample-cv">
              <Button variant="outline" className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white">
                {t("landing.pricing.sample")}
              </Button>
            </a>
            <Link to={user ? "/dashboard" : "/register"}>
              <Button className="bg-[#0F172A] hover:bg-[#1E293B]" data-testid="landing-cta-pricing">
                {user ? t("landing.pricing.buy") : t("landing.pricing.create")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ToolCard({ to, icon, title, copy, tag, openLabel, testId, isAr }) {
  return (
    <Link to={to} data-testid={testId}
      className="card-classic p-6 group hover:shadow-lg transition-all block">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-md bg-[#0F172A] text-white grid place-items-center group-hover:bg-[#881337] transition-colors">
          {icon}
        </div>
        <span className="pill">{tag}</span>
      </div>
      <h3 className="font-serif-display text-2xl font-semibold text-[#0F172A]">{title}</h3>
      <p className="mt-2 text-[#475569] leading-relaxed">{copy}</p>
      <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-[#0F172A] group-hover:text-[#881337]">
        {openLabel} <ArrowRight size={14} className={isAr ? "rotate-180" : ""}/>
      </div>
    </Link>
  );
}
